/**
 * Custom field rules over the sixteen live-tape fields, ported from the Top Premium Board's
 * field-filter builder. Each rule carries a tag saying where it runs: "server" rules were folded
 * into the QuestDB query, "client-side" rules filter the page that came back.
 */

import React from 'react';
import { CustomFilter, defaultOpForType, FilterOp, OP_LABELS, opsForType } from '../../utils/rtlScannerFlowRows';
import { CONTRACT_ROW_FIELDS, LIVE_TAPE_FIELDS, TAPE_FIELD_BY_KEY } from './tapeRules';

export interface RuleBuilderProps {
  rules: CustomFilter[];
  serverIds: Set<string>;
  contractsView: boolean;
  onChange: (next: CustomFilter[]) => void;
  newId: () => string;
}

const RuleBuilder: React.FC<RuleBuilderProps> = ({ rules, serverIds, contractsView, onChange, newId }) => {
  const add = () => {
    const def = TAPE_FIELD_BY_KEY.Premium ?? LIVE_TAPE_FIELDS[0];
    onChange([...rules, { id: newId(), field: def.key, op: defaultOpForType(def.type), value: '' }]);
  };

  const update = (id: string, patch: Partial<CustomFilter>) => {
    onChange(
      rules.map((f) => {
        if (f.id !== id) return f;
        const next = { ...f, ...patch };
        if (patch.field && patch.field !== f.field) {
          next.op = defaultOpForType(TAPE_FIELD_BY_KEY[patch.field]?.type ?? 'text');
          next.value = '';
        }
        return next;
      }),
    );
  };

  return (
    <div className="cfbar ftrules">
      <div className="cfhead">
        <span className="lab">Custom rules</span>
        <button type="button" className="chip" onClick={add}>
          + Add rule
        </button>
        {rules.length > 0 && (
          <button type="button" className="chip" onClick={() => onChange([])}>
            Clear
          </button>
        )}
        <span className="cfhint">
          Any of the {LIVE_TAPE_FIELDS.length} tape fields, all rules must match. Rules the server can answer run over
          the whole tape; the rest filter the loaded page and are marked client-side.
        </span>
      </div>
      {rules.length > 0 && (
        <div className="cflist">
          {rules.map((f) => {
            const def = TAPE_FIELD_BY_KEY[f.field];
            const type = def?.type ?? 'text';
            const empty = !f.value.trim();
            const skipped = contractsView && !CONTRACT_ROW_FIELDS.has(f.field);
            const where = empty ? 'not set' : serverIds.has(f.id) ? 'server' : skipped ? 'prints only' : 'client-side';
            return (
              <div className="cfrow" key={f.id}>
                <select className="sel" value={f.field} onChange={(e) => update(f.id, { field: e.target.value })}>
                  {LIVE_TAPE_FIELDS.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <select className="sel" value={f.op} onChange={(e) => update(f.id, { op: e.target.value as FilterOp })}>
                  {opsForType(type).map((op) => (
                    <option key={op} value={op}>
                      {OP_LABELS[op]}
                    </option>
                  ))}
                </select>
                {type === 'enum' ? (
                  <select className="sel" value={f.value} onChange={(e) => update(f.id, { value: e.target.value })}>
                    <option value="">any</option>
                    {(def?.options ?? []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="num mono"
                    type={type === 'number' ? 'number' : 'text'}
                    placeholder={type === 'number' ? 'value…' : 'text…'}
                    value={f.value}
                    onChange={(e) => update(f.id, { value: e.target.value })}
                  />
                )}
                <span
                  className={`ftwhere ${where.replace(/\s+/g, '-')}`}
                  title={
                    where === 'server'
                      ? 'Applied in QuestDB across every matching print'
                      : where === 'client-side'
                        ? 'No server parameter for this rule: it filters the rows already loaded'
                        : where === 'prints only'
                          ? 'Contract rows do not carry this field; the rule applies in Prints view'
                          : 'Enter a value to apply this rule'
                  }
                >
                  {where}
                </span>
                <button
                  type="button"
                  className="cfdel"
                  aria-label="remove rule"
                  onClick={() => onChange(rules.filter((r) => r.id !== f.id))}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RuleBuilder;
